import React from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";

import { LoginUser } from "../core/auth/type/login-user";
import { LoginUserProto } from "../core/auth/entity/login-user";
import { useLocalStorage } from "./use-local-storage";
import { initialState } from "../context/auth";
import { useUsecases } from "../store";
import { setPlatformUrl } from "../lib/api";
import { setIsNewDb } from "../lib/is-new-db";

export const useAuth = () => {
  const [cookies, , removeCookie] = useCookies(["token", "expire", "terms"]);
  const [token, setToken] = React.useState<string | null>(cookies.token);
  const [loginUser, setLoginUser] = React.useState<LoginUser>(initialState);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isLogin, setIsLogin] = React.useState<boolean>(false);
  const [needsReload, setNeedsReload] = React.useState<boolean>(false);

  const [, , , clear] = useLocalStorage<any | null>("all", null);
  const [commitHash, setCommitHash] = useLocalStorage<string>("commit-hash", "");

  const { authUsecase } = useUsecases();
  const queryClient = useQueryClient();
  const location = useLocation();

  const { mutate: fetchClient } = useMutation({
    mutationFn: () => {
      return authUsecase.fetchLoginUser({ token: token, ver: window._commit_hash });
    },
    onSuccess: (res) => {
      const user = res.user as LoginUser;
      const proto = new LoginUserProto(user);
      setLoginUser(user);
      // platform_url と ingest mode は同じ loginUser 応答 (client) 由来なので、
      // 同じ proto から同期セットして両者のズレを無くす (gateway が参照するため)。
      setPlatformUrl(proto.platformUrl);
      setIsNewDb(proto.isNewDb);
      setIsLoading(false);
      if (res.is_reload) {
        if (commitHash !== window._commit_hash) {
          setCommitHash(window._commit_hash??'');
          setNeedsReload(true);
        }
      } else {
        setCommitHash('');
      }
    },
    onError: () => {
      setIsLoading(false);
      logOut();
    },
  });

  React.useEffect(() => {
    if (!needsReload) return;
    // ログイン中はリロードしない
    if (location.pathname.includes("/login")) return;

    const timer = setTimeout(() => {
      window.location.reload();
    }, 500);
    return () => clearTimeout(timer);
  }, [needsReload, location.pathname]);

  const logOut = React.useCallback(() => {
    removeCookie("token", { path: "/" });
    removeCookie("expire", { path: "/" });
    removeCookie("terms", { path: "/" });

    clear();
    queryClient.clear();

    setIsLogin(false);
    setLoginUser(initialState);
    setPlatformUrl(null);
    setIsNewDb(false);
    setIsLoading(false);
  }, [clear, queryClient, removeCookie]);

  React.useEffect(() => {
    const token = cookies.token;
    const expire = cookies.expire ? new Date(cookies.expire) : null;
    const terms = cookies.terms === "true" || cookies.terms === true;

    if (!token || !terms || !expire || new Date() >= expire) {
      logOut();
      return;
    }
    setToken(token);
    setIsLogin(true);

    fetchClient();

    const interval = setInterval(() => {
      fetchClient();
    }, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [cookies, fetchClient, logOut]);

  return { token, loginUser, logOut, isLogin, isLoading, fetchClient };
};
